import {FC, useState} from 'react'
import {Outlet, NavLink} from 'react-router-dom'
import {useAuth} from '../../modules/auth'

const GREEN = '#039540'
const GREEN_DARK = '#026e2f'
const INK = '#1b1b29'
const TEXT = '#475569'

const NAV = [
  {to: '/housekeeping', label: 'Overview', end: true},
  {to: '/housekeeping/queue', label: 'Bed-wash queue', end: false},
  {to: '/housekeeping/log-visit', label: 'Log a visit', end: false},
  {to: '/housekeeping/earnings', label: 'Earnings', end: false},
  {to: '/housekeeping/payments', label: 'Payments', end: false},
  {to: '/housekeeping/door-codes', label: 'Door codes', end: false},
]

/**
 * Chrome for the housekeeping portal — a sticky white top nav mirroring the
 * public site header: brand left, section links centre, and an avatar + name
 * button (with a sign-out dropdown) on the right.
 */
const CleanerLayout: FC = () => {
  const {currentUser, logout} = useAuth()
  const [menu, setMenu] = useState(false)

  const name = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || 'Housekeeping'
  const initials =
    [currentUser?.first_name, currentUser?.last_name]
      .filter(Boolean)
      .map((n) => n![0]?.toUpperCase())
      .join('') || 'HK'

  const linkStyle = ({isActive}: {isActive: boolean}): React.CSSProperties => ({
    textDecoration: 'none',
    fontSize: 15,
    fontWeight: 600,
    color: isActive ? GREEN : TEXT,
    whiteSpace: 'nowrap',
  })

  return (
    <div style={{minHeight: '100vh', background: '#f4f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif'}}>
      <header style={{position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.92)', backdropFilter: 'saturate(180%) blur(8px)', borderBottom: '1px solid #eef0f4'}}>
        <div style={{maxWidth: 1140, margin: '0 auto', padding: '0 20px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16}}>
          {/* Brand */}
          <div style={{display: 'flex', alignItems: 'center', flexShrink: 0}}>
            <img src='/logo.png' alt='Crosscheck CrashPad' style={{height: 40, display: 'block'}} />
          </div>

          {/* Desktop nav (centre) */}
          <nav className='d-none d-lg-flex' style={{alignItems: 'center', gap: 26}}>
            {NAV.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} style={linkStyle}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Avatar + name → dropdown */}
          <div style={{position: 'relative', flexShrink: 0}}>
            <button
              onClick={() => setMenu((v) => !v)}
              aria-haspopup='menu'
              aria-expanded={menu}
              style={{display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '5px 14px 5px 5px', cursor: 'pointer'}}
            >
              <span style={{width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${GREEN}, ${GREEN_DARK})`, color: '#fff', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>{initials}</span>
              <span className='d-none d-sm-inline' style={{fontWeight: 600, fontSize: 14, color: INK}}>{name}</span>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#94a3b8' strokeWidth='2.5' style={{flexShrink: 0, transform: menu ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s'}}>
                <polyline points='6 9 12 15 18 9' />
              </svg>
            </button>
            {menu && (
              <>
                <div onClick={() => setMenu(false)} style={{position: 'fixed', inset: 0, zIndex: 60}} />
                <div role='menu' style={{position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 61, minWidth: 200, background: '#fff', border: '1px solid #eef0f4', borderRadius: 12, boxShadow: '0 12px 32px rgba(15,23,42,0.16)', padding: 6}}>
                  <div style={{padding: '8px 12px', borderBottom: '1px solid #f1f3f7', marginBottom: 4}}>
                    <div style={{fontWeight: 700, fontSize: 14, color: INK}}>{name}</div>
                    <div style={{fontSize: 12.5, color: '#7e8299', overflow: 'hidden', textOverflow: 'ellipsis'}}>{currentUser?.email}</div>
                  </div>
                  <button onClick={logout} style={{width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '9px 12px', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#d9214e'}}>
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile nav (scrollable row) */}
        <nav className='d-lg-none' style={{display: 'flex', gap: 8, overflowX: 'auto', padding: '0 16px 12px', maxWidth: 1140, margin: '0 auto'}}>
          {NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              style={({isActive}) => ({
                whiteSpace: 'nowrap', fontSize: 14, fontWeight: 700, textDecoration: 'none', borderRadius: 9, padding: '7px 13px',
                color: isActive ? '#fff' : TEXT, background: isActive ? GREEN : '#f1f3f7',
              })}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <div style={{maxWidth: 980, margin: '0 auto', padding: '24px 16px 60px'}}>
        <Outlet />
      </div>
    </div>
  )
}

export {CleanerLayout}
